// Layer health: turning individual tile outcomes into one honest status per
// source (BBB-457).
//
// WHY THIS IS NOT JUST "DID A TILE FAIL". A fused tile cache holds tiles only
// where its data is, so the Scotland-only caches 404 across sea tiles and across
// England and Wales as a matter of course. One failure therefore means nothing.
// One SUCCESS, on the other hand, proves the whole chain works: DNS, TLS, the
// service, the cache and the style. So the rule is ANY-SUCCESS-WINS and it
// latches: once a layer has drawn a single real tile it is `loaded` and stays
// that way for the life of the overlay instance.
//
// That leaves two honest failure shapes:
//
//   slow         requests are out, nothing has come back yet
//   unavailable  requests are out, tiles have failed, none has ever succeeded
//
// `unavailable` deliberately requires at least one failure. A server that
// accepts a connection and then hangs produces no failure at all, and calling
// that "unavailable" would be a guess; it stays `slow`, which is what the farmer
// is actually experiencing. It requires a failure, not an EARLY failure: one
// that lands after the deadline says exactly the same thing, so it publishes the
// same verdict rather than leaving the layer on `slow` for good.
//
// Thresholds are Davin's, set against what he saw on 19/08/2026: layers that
// work were "a lil slow", and the cold-zone freeze ran 1 to 8 seconds. So `slow`
// has to appear well inside a working load, and `unavailable` has to sit beyond
// the slowest one that still succeeds, or a working layer would be libelled.

export const SLOW_AFTER_MS = 3000;
export const UNAVAILABLE_AFTER_MS = 10000;

/**
 * One tracker per overlay instance. Timers are injected so the state machine can
 * be tested without fake timers and without waiting 10 real seconds.
 *
 * @param {object} args
 * @param {(status: string) => void} args.report called only when the status changes
 * @param {number} [args.slowAfterMs]
 * @param {number} [args.unavailableAfterMs]
 * @param {(fn: Function, ms: number) => any} [args.setTimer]
 * @param {(handle: any) => void} [args.clearTimer]
 * @returns {{requested: Function, succeeded: Function, failed: Function, dispose: Function}}
 */
export function createHealthTracker({
  report,
  slowAfterMs = SLOW_AFTER_MS,
  unavailableAfterMs = UNAVAILABLE_AFTER_MS,
  setTimer = setTimeout,
  clearTimer = clearTimeout,
} = {}) {
  let started = false;
  let loaded = false;
  let failures = 0;
  let status = null;
  let slowHandle = null;
  let unavailableHandle = null;
  // Whether the unavailable deadline has already come and gone. It used to be a
  // one-shot check inside its own timer, so a failure that arrived AFTER the
  // deadline was never assessed at all: the layer sat on `slow` for the rest of
  // the session, telling a farmer it was slow when it was in fact dead (BBB-527).
  // Rare for tiles, which fail in seconds and in dozens. Normal for a vector
  // layer, where one fetch either answers or fails and can easily do either well
  // past ten seconds.
  let deadlineReached = false;
  // Only timers could publish before a late failure could, so stopping them was
  // enough to guarantee silence once the overlay had gone. It no longer is.
  let disposed = false;

  function publish(next) {
    if (disposed || status === next) return;
    status = next;
    report?.(next);
  }

  function stopTimers() {
    if (slowHandle !== null) clearTimer(slowHandle);
    if (unavailableHandle !== null) clearTimer(unavailableHandle);
    slowHandle = null;
    unavailableHandle = null;
  }

  return {
    /**
     * A tile request has gone out. Only the first one starts the clocks: a layer
     * requests dozens of tiles per pan and restarting the clocks on each would
     * mean a layer being panned across never reaches a verdict.
     */
    requested() {
      // A disposed tracker must start nothing. `publish` is already silenced on
      // disposal, but without this the timers would still be created, so a
      // tracker whose overlay has gone would keep two live callbacks until they
      // fired into nothing. Guarded here rather than in the timer bodies so the
      // handles are never allocated in the first place.
      if (disposed || loaded || started) return;
      started = true;
      publish('loading');

      slowHandle = setTimer(() => {
        slowHandle = null;
        if (!loaded) publish('slow');
      }, slowAfterMs);

      unavailableHandle = setTimer(() => {
        unavailableHandle = null;
        deadlineReached = true;
        // No failure means nothing has been refused, only that nothing has
        // arrived. That is slow, not dead, and saying otherwise is a guess.
        if (!loaded && failures > 0) publish('unavailable');
      }, unavailableAfterMs);
    },

    succeeded() {
      if (loaded) return;
      loaded = true;
      stopTimers();
      publish('loaded');
    },

    failed() {
      if (loaded) return;
      failures += 1;
      // Past the deadline this is the same evidence as a failure that beat it,
      // so it earns the same verdict rather than being dropped on the floor.
      if (deadlineReached) publish('unavailable');
    },

    /**
     * Overlay removed from the map: drop the timers so they cannot fire late,
     * and stop publishing, because a failure can now arrive after the layer has
     * gone and a layer turned off while loading must not announce itself
     * unavailable seconds later.
     */
    dispose() {
      disposed = true;
      stopTimers();
    },
  };
}
